import React, { useState, useEffect } from "react"
import {
  useLazyGetOrgDeletionImpactQuery,
  useDeleteOrganizationMutation,
  OrgDeletionImpact,
} from "../app/services/meseeknaApiV2"
import { FiAlertTriangle, FiX, FiTrash2, FiChevronRight } from "react-icons/fi"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AlertIcon = (props: { size?: number; className?: string }): JSX.Element => <FiAlertTriangle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CloseIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const TrashIcon = (props: { size?: number; className?: string }): JSX.Element => <FiTrash2 {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronRight {...props} />

type OrgDeletionModalProps = {
  isOpen: boolean
  organizationId: number
  organizationName: string
  onClose: () => void
  onDeleted: () => void
}

const OrgDeletionModal: React.FC<OrgDeletionModalProps> = ({
  isOpen,
  organizationId,
  organizationName,
  onClose,
  onDeleted,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [confirmationInput, setConfirmationInput] = useState("")
  const [impactData, setImpactData] = useState<OrgDeletionImpact | null>(null)

  const [fetchImpact, { isLoading: isLoadingImpact }] = useLazyGetOrgDeletionImpactQuery()
  const [deleteOrg, { isLoading: isDeleting }] = useDeleteOrganizationMutation()

  // Fetch impact data when modal opens
  useEffect(() => {
    if (isOpen && organizationId) {
      fetchImpact(organizationId)
        .unwrap()
        .then((response) => {
          setImpactData(response.data)
        })
        .catch((err) => {
          console.error("Failed to fetch deletion impact:", err)
        })
    }
    // Reset state when modal opens/closes
    if (!isOpen) {
      setStep(1)
      setConfirmationInput("")
      setImpactData(null)
    }
  }, [isOpen, organizationId, fetchImpact])

  const handleNextStep = () => {
    if (step === 1) {
      setStep(2)
    } else if (step === 2 && confirmationInput === organizationName) {
      setStep(3)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteOrg({ organizationId, confirmationName: confirmationInput }).unwrap()
      onDeleted()
      onClose()
    } catch (err) {
      console.error("Failed to delete organization:", err)
    }
  }

  if (!isOpen) return null

  const totalRecordsAffected = impactData
    ? impactData.impact.userCount +
      impactData.impact.assignedTestsCount +
      impactData.impact.rawScoresCount +
      impactData.impact.userNotesCount +
      impactData.impact.organizationMeasuresCount +
      impactData.impact.groupsCount +
      impactData.impact.subscriptionsCount
    : 0

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-red-50">
          <div className="flex items-center gap-2 text-red-700">
            <AlertIcon size={24} />
            <h2 className="text-lg font-semibold">Delete Organization</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step indicators */}
          <div className="flex items-center justify-center mb-6">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s
                      ? "bg-red-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div
                    className={`w-12 h-1 mx-1 ${
                      step > s ? "bg-red-600" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Loading state */}
          {isLoadingImpact && (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-red-200 border-t-red-600 rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">Loading impact data...</span>
            </div>
          )}

          {/* Step 1: Confirm Intent */}
          {!isLoadingImpact && step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Are you sure you want to delete this organization?
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="font-medium text-gray-900">{organizationName}</p>
                {impactData?.organization.createdOn && (
                  <p className="text-sm text-gray-500 mt-1">
                    Created: {new Date(impactData.organization.createdOn).toLocaleDateString()}
                  </p>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-yellow-800 mb-2">This action will:</h4>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>Remove organization association from {impactData?.impact.userCount || 0} user(s)</li>
                  <li>Delete {impactData?.impact.rawScoresCount || 0} raw score record(s)</li>
                  <li>Delete {impactData?.impact.organizationMeasuresCount || 0} measure access record(s)</li>
                  <li>Delete {impactData?.impact.groupsCount || 0} organization group(s)</li>
                  <li>Delete {impactData?.impact.userNotesCount || 0} user note(s)</li>
                </ul>
              </div>

              {impactData?.warnings && impactData.warnings.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-red-800 mb-2">Warnings:</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    {impactData.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Type org name to confirm */}
          {!isLoadingImpact && step === 2 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Type the organization name to confirm
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please type <span className="font-semibold text-gray-900">{organizationName}</span> to confirm deletion.
              </p>
              <input
                type="text"
                value={confirmationInput}
                onChange={(e) => setConfirmationInput(e.target.value)}
                placeholder="Type organization name..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
              {confirmationInput.length > 0 && confirmationInput !== organizationName && (
                <p className="text-sm text-red-600 mt-2">
                  Name does not match. Please type exactly: {organizationName}
                </p>
              )}
            </div>
          )}

          {/* Step 3: Final warning */}
          {!isLoadingImpact && step === 3 && (
            <div>
              <div className="bg-red-100 border border-red-300 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-red-800 mb-2">
                  <AlertIcon size={20} />
                  <h3 className="font-semibold">Final Warning</h3>
                </div>
                <p className="text-red-700">
                  You are about to permanently delete <strong>{organizationName}</strong> and all associated data.
                  This action <strong>cannot be undone</strong>.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Deletion Summary:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Users affected:</span>
                    <span className="font-medium">{impactData?.impact.userCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Simulations:</span>
                    <span className="font-medium">{impactData?.impact.assignedTestsCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Team simulations:</span>
                    <span className="font-medium">{impactData?.impact.teamSimulationsCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Raw scores:</span>
                    <span className="font-medium">{impactData?.impact.rawScoresCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Measures:</span>
                    <span className="font-medium">{impactData?.impact.organizationMeasuresCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Groups:</span>
                    <span className="font-medium">{impactData?.impact.groupsCount || 0}</span>
                  </div>
                </div>
                <div className="border-t mt-3 pt-3">
                  <div className="flex justify-between text-sm font-semibold">
                    <span className="text-gray-900">Total records affected:</span>
                    <span className="text-red-600">{totalRecordsAffected}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            disabled={isDeleting}
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {step === 1 && (
              <button
                onClick={handleNextStep}
                disabled={isLoadingImpact}
                className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                Continue
                <ChevronIcon size={16} />
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleNextStep}
                disabled={confirmationInput !== organizationName}
                className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                Continue
                <ChevronIcon size={16} />
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon size={16} />
                    Delete Organization
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrgDeletionModal
